import { parseMarkdown, type Span } from '@fp/core/content/markdown';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { Topbar } from '../components/Shell';
import { Confirm, Empty, ErrorBox, Field, Loading, Seg, useAction, useToast } from '../components/ui';
import { del, get, patch, post, qs } from '../lib/api';
import { fmtAgo, fmtDateTime } from '../lib/format';
import type { ArticleAdmin, ArticleKind, MatchRow } from '../lib/types';

export const KIND_LABEL: Record<ArticleKind, string> = { PREVIEW: 'Preview', REPORT: 'Report', NEWS: 'News' };

export function useAiStatus() {
  return useQuery({ queryKey: ['articles', 'ai'], queryFn: () => get<{ configured: boolean }>('/admin/articles/ai'), staleTime: 60_000 });
}

/** Where an article stands, in a pill. */
export function ArticleState({ a }: { a: Pick<ArticleAdmin, 'status' | 'aiState' | 'aiError'> & Partial<Pick<ArticleAdmin, 'verificationState' | 'reviewedAt' | 'aiWritten'>> }) {
  if (a.aiState === 'QUEUED') return <span className="pill pill-amber">AI: waiting</span>;
  if (a.aiState === 'RUNNING') return <span className="pill pill-amber">AI: writing…</span>;
  if (a.aiState === 'FAILED') return <span className="pill pill-red" title={a.aiError ?? ''}>AI failed</span>;
  return (
    <span className="row" style={{ gap: 4, display: 'inline-flex' }}>
      {a.status === 'PUBLISHED' ? <span className="pill pill-green">Published</span> : <span className="pill">Draft</span>}
      <CheckPill a={a} />
    </span>
  );
}

/** The fact check's result and the review, for AI articles. */
export function CheckPill({ a }: { a: Partial<Pick<ArticleAdmin, 'verificationState' | 'reviewedAt'>> }) {
  if (a.reviewedAt) return <span className="pill pill-green" title="An editor approved it">Reviewed</span>;
  if (a.verificationState === 'FAILED') return <span className="pill pill-red" title="Contradicts its match data: can't be published until fixed">Check failed</span>;
  if (a.verificationState === 'NEEDS_REVIEW') return <span className="pill pill-amber" title="Claims the data can't confirm">Needs review</span>;
  if (a.verificationState === 'PASSED') return <span className="pill" title="Passed every automatic check; not reviewed yet">Checked</span>;
  return null;
}

// ================================================================ list

export function ArticlesPage() {
  const search = useSearch({ strict: false }) as { q?: string; kind?: string; status?: string; review?: string };
  const navigate = useNavigate();
  const set = (p: typeof search) => navigate({ to: '/articles', search: { ...search, ...p } });
  const q = useQuery({
    queryKey: ['articles', search],
    queryFn: () => get<{ articles: ArticleAdmin[] }>(`/admin/articles${qs(search)}`),
    // Keep AI progress moving.
    refetchInterval: (query) => (query.state.data?.articles.some((a) => a.aiState === 'QUEUED' || a.aiState === 'RUNNING') ? 3000 : false),
  });
  const [text, setText] = useState(search.q ?? '');
  return (
    <>
      <Topbar
        title="Articles"
        sub="Previews, reports and news on the player site. The AI writes a preview for every imported match; you can edit or rewrite any article."
        actions={
          <Link to="/articles/new" className="btn btn-primary">
            New article
          </Link>
        }
      />
      <div className="page">
        <div className="card">
          <form
            className="card-head"
            onSubmit={(e) => {
              e.preventDefault();
              set({ q: text || undefined });
            }}
          >
            <input className="input" style={{ width: 240 }} placeholder="Search titles or teams" aria-label="Search" value={text} onChange={(e) => setText(e.target.value)} />
            <select className="select" aria-label="Type" value={search.kind ?? ''} onChange={(e) => set({ kind: e.target.value || undefined })}>
              <option value="">All types</option>
              {(Object.keys(KIND_LABEL) as ArticleKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
            <select className="select" aria-label="Status" value={search.status ?? ''} onChange={(e) => set({ status: e.target.value || undefined })}>
              <option value="">Published and drafts</option>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Drafts</option>
            </select>
            <label className="check">
              <input type="checkbox" checked={search.review === '1'} onChange={(e) => set({ review: e.target.checked ? '1' : undefined })} /> Needs a look only
            </label>
            <button className="btn">Search</button>
          </form>
          {q.error && <ErrorBox error={q.error} />}
          {q.isLoading ? (
            <Loading />
          ) : q.data?.articles.length === 0 ? (
            <Empty>No articles yet. Import matches from the data feed (the AI writes their previews) or write one.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Match</th>
                    <th>State</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data?.articles.map((a) => (
                    <tr key={a.id} className="clickable" onClick={() => navigate({ to: '/articles/$articleId', params: { articleId: String(a.id) } })}>
                      <td>
                        <strong>{a.title}</strong> {a.featured && <span className="pill">Featured</span>}
                        {a.aiWritten && <span className="muted small"> · written by AI</span>}
                      </td>
                      <td>{KIND_LABEL[a.kind]}</td>
                      <td className="muted">{a.homeName ? `${a.homeName} v ${a.awayName} · ${fmtDateTime(a.kickoffAt)}` : '—'}</td>
                      <td>
                        <ArticleState a={a} />
                      </td>
                      <td className="muted small">{fmtAgo(a.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ================================================================ editor

function Spans({ spans }: { spans: Span[] }) {
  return <>{spans.map((s, i) => (s.bold ? <strong key={i}>{s.text}</strong> : s.italic ? <em key={i}>{s.text}</em> : <Fragment key={i}>{s.text}</Fragment>))}</>;
}

/** The body as the player site shows it (same parser; elements only, no HTML). */
export function ArticlePreview({ body }: { body: string }) {
  const blocks = parseMarkdown(body);
  if (!blocks.length) return <Empty>Nothing written yet.</Empty>;
  return (
    <div className="stack" style={{ gap: 10, lineHeight: 1.55 }}>
      {blocks.map((b, i) =>
        b.type === 'h2' ? (
          <h2 key={i} style={{ margin: '6px 0 0' }}>
            <Spans spans={b.spans} />
          </h2>
        ) : b.type === 'h3' ? (
          <h3 key={i} style={{ margin: 0 }}>
            <Spans spans={b.spans} />
          </h3>
        ) : b.type === 'quote' ? (
          <blockquote key={i} style={{ margin: 0, paddingLeft: 12, borderLeft: '3px solid var(--line)' }} className="muted">
            <Spans spans={b.spans} />
          </blockquote>
        ) : b.type === 'p' ? (
          <p key={i} style={{ margin: 0 }}>
            <Spans spans={b.spans} />
          </p>
        ) : 'items' in b ? (
          b.type === 'ol' ? (
            <ol key={i} style={{ margin: 0 }}>
              {b.items.map((it, k) => (
                <li key={k}>
                  <Spans spans={it} />
                </li>
              ))}
            </ol>
          ) : (
            <ul key={i} style={{ margin: 0 }}>
              {b.items.map((it, k) => (
                <li key={k}>
                  <Spans spans={it} />
                </li>
              ))}
            </ul>
          )
        ) : null,
      )}
    </div>
  );
}

type Form = { kind: ArticleKind; matchId: number | null; matchLabel: string | null; title: string; summary: string; body: string; status: 'DRAFT' | 'PUBLISHED'; featured: boolean };

const formOf = (a: ArticleAdmin): Form => ({
  kind: a.kind,
  matchId: a.matchId,
  matchLabel: a.homeName ? `${a.homeName} v ${a.awayName}` : null,
  title: a.title,
  summary: a.summary,
  body: a.body ?? '',
  status: a.status,
  featured: a.featured,
});

function MatchPicker({ value, label, onChange }: { value: number | null; label: string | null; onChange: (id: number | null, label: string | null) => void }) {
  const [q, setQ] = useState('');
  const found = useQuery({
    queryKey: ['matches', 'pick', q],
    queryFn: () => get<{ matches: MatchRow[] }>(`/admin/matches${qs({ q, limit: 12 })}`),
    enabled: q.trim().length > 1,
  });
  if (value) {
    return (
      <div className="row" style={{ gap: 8 }}>
        <span className="pill pill-dark">{label ?? `Match #${value}`}</span>
        <Link to="/matches/$matchId" params={{ matchId: String(value) }} className="btn btn-sm">
          Open
        </Link>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange(null, null)}>
          Change
        </button>
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <input className="input" placeholder="Search a team or competition" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find the match" />
      {found.data?.matches.map((m) => (
        <button key={m.id} type="button" className="btn btn-sm" style={{ justifyContent: 'flex-start' }} onClick={() => onChange(m.id, `${m.homeName} v ${m.awayName}`)}>
          {m.homeName} v {m.awayName} · {m.competitionName} · {fmtDateTime(m.kickoffAt)}
        </button>
      ))}
    </div>
  );
}

export function ArticleEditPage() {
  const { articleId } = useParams({ strict: false }) as { articleId?: string };
  const search = useSearch({ strict: false }) as { matchId?: number; kind?: string };
  const isNew = !articleId;
  const id = Number(articleId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const ai = useAiStatus();
  const q = useQuery({
    queryKey: ['article', id],
    queryFn: () => get<{ article: ArticleAdmin }>(`/admin/articles/${id}`),
    enabled: !isNew,
    refetchInterval: (query) => {
      const s = query.state.data?.article.aiState;
      return s === 'QUEUED' || s === 'RUNNING' ? 2000 : false;
    },
  });
  const [form, setForm] = useState<Form | null>(
    isNew
      ? {
          kind: search.kind === 'PREVIEW' || search.kind === 'REPORT' ? search.kind : 'NEWS',
          matchId: search.matchId ?? null,
          matchLabel: null,
          title: '',
          summary: '',
          body: '',
          status: 'DRAFT',
          featured: false,
        }
      : null,
  );
  const [dirty, setDirty] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [view, setView] = useState<'write' | 'preview'>('write');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();
  const a = q.data?.article;

  // Server copy in: on load, and when the AI delivers (unless there are unsaved edits).
  useEffect(() => {
    if (a && (!form || !dirty)) setForm(formOf(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.updatedAt, a?.aiState]);

  if (!isNew && q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  if (!form) return <Loading />;
  const change = (p: Partial<Form>) => {
    setForm({ ...form, ...p });
    setDirty(true);
  };
  const payload = () => ({ kind: form.kind, matchId: form.matchId, title: form.title, summary: form.summary, body: form.body, status: form.status, featured: form.featured });
  const writing = a?.aiState === 'QUEUED' || a?.aiState === 'RUNNING';
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['articles'] });
    await qc.invalidateQueries({ queryKey: ['article', id] });
  };

  const save = () =>
    run(async () => {
      if (isNew) {
        const r = await post<{ article: ArticleAdmin }>('/admin/articles', payload());
        await qc.invalidateQueries({ queryKey: ['articles'] });
        setDirty(false);
        navigate({ to: '/articles/$articleId', params: { articleId: String(r.article.id) } });
      } else {
        const r = await patch<{ article: ArticleAdmin }>(`/admin/articles/${id}`, payload());
        setDirty(false);
        setForm(formOf(r.article));
        await refresh();
      }
    }, 'Saved');

  const askAi = () =>
    run(async () => {
      if (isNew) {
        // Create it and hand it to the AI in one go.
        const r = await post<{ article: ArticleAdmin }>('/admin/articles', { ...payload(), title: form.title || 'Draft (AI is writing)', status: 'DRAFT', ai: true, instructions });
        await qc.invalidateQueries({ queryKey: ['articles'] });
        setDirty(false);
        navigate({ to: '/articles/$articleId', params: { articleId: String(r.article.id) } });
        return;
      }
      if (dirty) await patch(`/admin/articles/${id}`, payload());
      setDirty(false);
      await post(`/admin/articles/${id}/ai`, { instructions });
      setInstructions('');
      await refresh();
    }, 'The AI is on it');

  return (
    <>
      <Topbar
        title={isNew ? 'New article' : form.title || 'Article'}
        sub={
          a ? (
            <span className="row" style={{ gap: 6 }}>
              <ArticleState a={a} /> {KIND_LABEL[a.kind]} · updated {fmtAgo(a.updatedAt)}
              {a.aiWritten && a.aiModel && <span className="muted"> · written by AI ({a.aiModel})</span>}
              {a.status === 'PUBLISHED' && <span className="muted"> · /news/{a.slug}</span>}
            </span>
          ) : (
            'Write it yourself, or give the AI some notes'
          )
        }
        actions={
          <>
            {!isNew && (
              <button type="button" className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                Delete
              </button>
            )}
            <button type="button" className="btn btn-primary" disabled={busy || writing || !form.title.trim()} onClick={() => void save()}>
              {isNew ? 'Create' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </>
        }
      />
      <div className="page">
        <div className="cols cols-main">
          <section className="card">
            <div className="card-body stack" style={{ gap: 16 }}>
              {a?.aiState === 'FAILED' && <div className="err-box">The AI couldn't write it: {a.aiError}</div>}
              {writing && <div className="note-box">The AI is writing this article. It appears here when it's done (usually under a minute); editing now cancels it.</div>}
              <div className="form-grid">
                <Field label="Type">
                  <select className="select" value={form.kind} onChange={(e) => change({ kind: e.target.value as ArticleKind })}>
                    <option value="NEWS">News (any topic)</option>
                    <option value="PREVIEW">Match preview</option>
                    <option value="REPORT">Match report</option>
                  </select>
                </Field>
                <div className="field">
                  <span>{form.kind === 'NEWS' ? 'Match (optional)' : 'Match'}</span>
                  <MatchPicker value={form.matchId} label={form.matchLabel} onChange={(matchId, matchLabel) => change({ matchId, matchLabel })} />
                </div>
              </div>
              <Field label="Title" hint={`${form.title.length}/140`}>
                <input className="input" value={form.title} maxLength={140} onChange={(e) => change({ title: e.target.value })} />
              </Field>
              <Field label="Summary" hint="One or two sentences under the title, and on news cards">
                <textarea className="textarea" rows={2} maxLength={400} value={form.summary} onChange={(e) => change({ summary: e.target.value })} />
              </Field>
              <div className="stack" style={{ gap: 8 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Article</span>
                  <Seg label="View" value={view} onChange={setView} options={[['write', 'Write'], ['preview', 'Preview']]} />
                </div>
                {view === 'write' ? (
                  <>
                    <textarea
                      className="textarea"
                      rows={18}
                      value={form.body}
                      onChange={(e) => change({ body: e.target.value })}
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
                    />
                    <span className="hint muted small">Blank line between paragraphs · ## Heading · - list · **bold** · *italic*</span>
                  </>
                ) : (
                  <div className="card-body" style={{ border: '1px solid var(--line)', borderRadius: 10 }}>
                    <ArticlePreview body={form.body} />
                  </div>
                )}
              </div>
              <div className="row" style={{ gap: 18 }}>
                <Seg label="Status" value={form.status} onChange={(status) => change({ status })} options={[['DRAFT', 'Draft'], ['PUBLISHED', 'Published']]} />
                <label className="check">
                  <input type="checkbox" checked={form.featured} onChange={(e) => change({ featured: e.target.checked })} /> Featured (top of the landing page and the news list)
                </label>
              </div>
            </div>
          </section>

          <aside className="stack" style={{ gap: 20 }}>
            {a && (a.verificationState || a.aiModel) && (
              <FactCheck
                a={a}
                busy={busy || writing || dirty}
                unsaved={dirty}
                onApprove={() =>
                  run(async () => {
                    await post(`/admin/articles/${id}/approve`, {});
                    await refresh();
                  }, 'Approved and published')
                }
                onCheck={() =>
                  run(async () => {
                    await post(`/admin/articles/${id}/check`, {});
                    await refresh();
                  }, 'Checked again')
                }
              />
            )}
            <section className="card" aria-labelledby="ai-h">
              <div className="card-head">
                <h2 id="ai-h">{form.body.trim() ? 'Rewrite with AI' : 'Write with AI'}</h2>
              </div>
              <div className="card-body stack">
                {ai.data && !ai.data.configured && (
                  <div className="note-box">
                    The AI writer isn't configured: set <code>ANTHROPIC_API_KEY</code> for the API. Requests wait until it is.
                  </div>
                )}
                <span className="muted small">
                  {form.matchId
                    ? "The AI writes from the match facts (form, head to head, lineups, injuries, the result and stats once played), and the result is checked against them. It's published on its own unless it contradicts them."
                    : 'Without a match, the AI writes only from your notes.'}
                </span>
                <Field label={form.body.trim() ? 'What to change' : form.matchId ? 'Notes (optional)' : 'Notes'}>
                  <textarea
                    className="textarea"
                    rows={5}
                    maxLength={4000}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder={form.body.trim() ? 'e.g. Shorter, and lead with the injury news' : 'e.g. Announce the new season of the game: the competitions it follows, when week 1 opens...'}
                  />
                </Field>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || writing || (!form.matchId && !instructions.trim() && !form.body.trim()) || (form.kind !== 'NEWS' && !form.matchId)}
                  onClick={() => void askAi()}
                >
                  {writing ? 'Writing…' : form.body.trim() ? 'Rewrite' : 'Write it'}
                </button>
                <span className="muted small">Your edits are saved first. The result stays a draft unless the article is already published.</span>
              </div>
            </section>
          </aside>
        </div>
      </div>
      {confirmDelete && a && (
        <Confirm
          title={`Delete “${a.title}”?`}
          body="It disappears from the player site. This can't be undone."
          confirmLabel="Delete"
          danger
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/admin/articles/${id}`);
            await qc.invalidateQueries({ queryKey: ['articles'] });
            toast('Article deleted');
            navigate({ to: '/articles' });
          }}
        />
      )}
    </>
  );
}

/**
 * The automatic fact check of an AI article (content/verify.ts), what it was written
 * from, and the editor's decision. Approving publishes it and marks it reviewed; an
 * article that contradicts its data can't be approved until the text is fixed (saving
 * re-runs the check).
 */
function FactCheck({ a, busy, unsaved, onApprove, onCheck }: { a: ArticleAdmin; busy: boolean; unsaved: boolean; onApprove: () => void; onCheck: () => void }) {
  const issues = a.verification?.issues ?? [];
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  return (
    <section className="card" aria-labelledby="check-h">
      <div className="card-head">
        <h2 id="check-h">Fact check and review</h2>
        <CheckPill a={a} />
      </div>
      <div className="card-body stack" style={{ gap: 10 }}>
        {a.verificationState === 'PASSED' && <span className="small">Passed every automatic check against the match data.</span>}
        {a.verificationState === 'FAILED' && <div className="err-box">It contradicts the match data, so it wasn't published. Fix the text (saving re-runs the check) or ask the AI to rewrite it.</div>}
        {a.verificationState === 'NEEDS_REVIEW' && <div className="note-box">Published, but some claims can't be confirmed from the match data: worth a look.</div>}
        {!a.verificationState && <span className="small muted">Not checked yet.</span>}
        {errors.length > 0 && (
          <ul className="small" style={{ margin: 0, paddingLeft: 18, color: 'var(--red)' }}>
            {errors.map((i, k) => (
              <li key={k}>{i.message}</li>
            ))}
          </ul>
        )}
        {warnings.length > 0 && (
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((i, k) => (
              <li key={k}>{i.message}</li>
            ))}
          </ul>
        )}
        {a.writerRecommendation && (
          <div className="small">
            <strong>The writer recommends: </strong>
            {{ READY_FOR_REVIEW: 'ready for review', NEEDS_CHANGES: 'changes needed', DO_NOT_PUBLISH: 'not publishing' }[a.writerRecommendation]}
            {a.writerReason && <span className="muted"> · {a.writerReason}</span>}
          </div>
        )}
        {!!a.sourcesUsed?.length && (
          <div className="small">
            <strong>Sources it used</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {a.sourcesUsed.map((x, k) => (
                <li key={k}>
                  <span className="muted">{x.id === 'match-record' ? 'Match record' : x.id === 'data-provider' ? 'Data provider' : x.id}:</span> {x.facts}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!!a.editorNotes?.length && (
          <div className="small">
            <strong>The writer's quality findings</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {a.editorNotes.map((n, k) => (
                <li key={k}>{n}</li>
              ))}
            </ul>
          </div>
        )}
        <span className="small muted">
          {a.dataCapturedAt ? `Written from ${a.dataProvider ?? 'the match data'} captured ${fmtDateTime(a.dataCapturedAt)}.` : a.sourcesNote ?? ''}
          {a.verification?.checkedAt && ` Checked ${fmtAgo(a.verification.checkedAt)}${a.verification.evidence.length ? ` · uses: ${a.verification.evidence.join(', ')}` : ''}.`}
        </span>
        <span className="small muted">
          {a.reviewedAt ? `Reviewed ${fmtDateTime(a.reviewedAt)}.` : 'Not reviewed by an editor.'}
        </span>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" disabled={busy || a.verificationState === 'FAILED' || !!a.reviewedAt} onClick={onApprove}>
            {a.status === 'PUBLISHED' ? 'Mark as reviewed' : 'Approve and publish'}
          </button>
          {a.matchId && (
            <button type="button" className="btn" disabled={busy} onClick={onCheck}>
              Check again
            </button>
          )}
        </div>
        {unsaved && <span className="hint muted small">Save your changes first (saving re-runs the check).</span>}
      </div>
    </section>
  );
}
